import React from 'react';
import { type FormikProps } from 'formik';

import { useMode } from '../../../../../contexts';
import { PAGE_TEXT as ACADEMIC_TEXT } from '../../../../Application/components/ApplicationForm/lang';
import type { AcademicQualification as ApplicationAcademicQualification } from '../../../../Application/graphql';
import { AssessmentAccordion } from '../../../../Assessment';
import {
  AR_QUALIFICATION_CHECK_TEXT,
  QUALIFICATION_PAGE_TEXT,
} from '../../../lang';
import type {
  AcademicQualification as AcademicQualificationType,
  QualificationCheck,
} from '../../../graphql';
import { QualificationDetailMode } from '../types';
import { type ValidationItem, QualificationItem } from '../Components';
import { getAcademicMap, getApplicantAcademicMap } from './getAcademicMap';

const CODES_TEXT = ACADEMIC_TEXT.QUALIFICATION.ACADEMIC_QUALIFICATION;
const { ONLINE_VERIFICATION, DEGREE_VERIFICATION } = CODES_TEXT;

export type AcademicQualificationCheckProps = {
  academicQualifications?: Array<
    ApplicationAcademicQualification & AcademicQualificationType
  >;
  formik: FormikProps<QualificationCheck>;
};

const defaultValue = AR_QUALIFICATION_CHECK_TEXT.WILL_UPDATE;
const noValueDisplay = AR_QUALIFICATION_CHECK_TEXT.NA;

export const AcademicQualificationCheck: React.FunctionComponent<
  AcademicQualificationCheckProps
> = props => {
  const { academicQualifications, formik } = props;

  const { mode } = useMode();
  const readonly =
    mode === QualificationDetailMode.APPLICANT_VIEW ||
    mode === QualificationDetailMode.MONITOR_VIEW;
  const isApplicantView = mode === QualificationDetailMode.APPLICANT_VIEW;

  const getAcademicValidationData = (
    formik: FormikProps<QualificationCheck>,
    index: number,
    isChineseMainlandExperience: boolean,
    readonly: boolean,
  ): ValidationItem[] => {
    const academicOnlineCodeName = `academicQualification[${index}].academicCredentialCheckPassed`;
    const academicOnlineCodeError =
      formik.touched.academicQualification &&
      Boolean(formik.errors.academicQualification?.[index]);
    const academicOnlineCodeHelperText =
      formik.touched.academicQualification &&
      formik.errors.academicQualification?.[index];

    const degreeOnlineCodeName = `academicQualification[${index}].degreeCertificationCheckPassed`;
    const degreeOnlineCodeError =
      formik.touched.academicQualification &&
      Boolean(formik.errors.academicQualification?.[index]);
    const degreeOnlineCodeHelperText =
      formik.touched.academicQualification &&
      formik.errors.academicQualification?.[index];

    const statusName = `academicQualification[${index}].status`;
    const statusError =
      formik.touched.academicQualification &&
      Boolean(formik.errors.academicQualification?.[index]);
    const statusHelperText =
      formik.touched.academicQualification &&
      formik.errors.academicQualification?.[index];

    const formikValue = formik.values.academicQualification?.[index];

    const verificationData: ValidationItem[] = isChineseMainlandExperience
      ? [
          {
            label: ONLINE_VERIFICATION,
            value: formikValue?.academicCredentialCheckPassed,
            name: academicOnlineCodeName,
            error: academicOnlineCodeError,
            helperText: academicOnlineCodeHelperText,
            readonly,
          },
          {
            label: DEGREE_VERIFICATION,
            value: formikValue?.degreeCertificationCheckPassed,
            name: degreeOnlineCodeName,
            error: degreeOnlineCodeError,
            helperText: degreeOnlineCodeHelperText,
            readonly,
          },
        ]
      : [
          {
            label: '',
            value: formikValue?.status,
            name: statusName,
            error: statusError,
            helperText: statusHelperText,
            readonly,
          },
        ];
    return verificationData;
  };

  return (
    <AssessmentAccordion
      title={QUALIFICATION_PAGE_TEXT.ACADEMIC_CHECK}
      titlecolor="#951D1D"
      defaultExpanded
      AssessDetails={
        <>
          {(academicQualifications?.length ?? 0)
            ? academicQualifications?.map((item, index) => {
                // converted country
                const isChineseMainlandExperience =
                  item?.institution?.country === 'China';

                const academicMap = isApplicantView
                  ? getApplicantAcademicMap(item)
                  : getAcademicMap(item);
                const validationData = getAcademicValidationData(
                  formik,
                  index,
                  isChineseMainlandExperience,
                  readonly,
                );

                const informationDisplayProps = {
                  blockData: academicMap,
                  defaultValue,
                };
                const qualificationValidationProps = {
                  validationItems: validationData,
                  handleBlur: formik.handleBlur,
                  handleChange: formik.handleChange,
                };
                return (
                  <QualificationItem
                    key={index}
                    index={index}
                    title={`${CODES_TEXT.TITLE} - ${index + 1}`}
                    informationDisplayProps={informationDisplayProps}
                    qualificationValidationProps={qualificationValidationProps}
                  />
                );
              })
            : noValueDisplay}
        </>
      }
    />
  );
};
